import { Button, Card, Flex, Loader, TextArea, ThemePrepared } from '@fluentui/react-northstar';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { useHistory } from 'react-router-dom';
import rehypeRaw from 'rehype-raw'; // IMPORTANT: DO NOT USE WITHOUT DOM-PURIFY. This could lead to script injection.
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { appState, useSetState } from '../../AppState';
import { getCachedUser } from '../../shared/cache/PlatformUserCache';
import { startFileUploadSession } from '../../shared/components/EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent';
import { TicketCustomFormSection } from '../../shared/components/TicketCustomFormTab/TicketCustomFormSection';
import { platformService } from '../../shared/services/platform.service';
import { uploadService } from '../../shared/services/upload.service';
import { FILTERS_STRINGS, markdownComponents, STATUS_GUID } from '../../shared/utils/constants';
import { parseIfJSON } from '../../shared/utils/helper';
import { UploadFile } from '../../templates/CustomComponents/UploadFile';
import { CustomFormAttachmentsProps } from '../../templates/CustomForms/CustomForm.Interface';
import { markFormsAsUnanswerd, parseCardTemplate, setCustomFormTemplateData, updateCustomFormListWithAnswers, validateRequiredInputs } from '../../templates/CustomForms/CustomForm.Service';
import { LabelElement } from '../../token/TokenModal';
import { getPortalSource, getUserFromToken } from '../ticketHelper';
import useActionButtonsFlex from '../useActionButtonsFlex';
import { CreateRequest_TriggerExecuteButton, CreateRequestAdaptiveCard, FormatCardAnswerJson, RemoveRequest_ActionSet } from './CreateRequest';
import { ScopeErrorMessage } from '../../Rbac/ScopePermissionNoAccess';

interface Props {
  globalTheme: ThemePrepared<any>;
  onCreateTicket?: () => void;
  onCreateComplete?: () => void;
  teamsConfig?: any;
  onCancel?:()=>void;
}

export const NewRequest = ({
  globalTheme,
  onCreateTicket,
  onCreateComplete,
  teamsConfig,
  onCancel
}: Props) => {
  const api = new platformService();
  const uploadHelper = new uploadService();
  const history = useHistory();
  const setAppState = useSetState();
  const actionsFlexProps = useActionButtonsFlex();
  const currentState = appState();
  const { t } = useTranslation();

  const [request, setRequest] = useState("");
  const [state, setState] = useState(teamsConfig?.RequestBehavior === "CreateTicket" ? 5 : 0);
  const [answer, setAnswer] = useState("");
  const [templateId, setTemplate] = useState<number | null>(null);
  const [templateResponse, setTemplateResponse] = useState({});
  const [isLoading, setLoading] = useState(teamsConfig?.RequestBehavior === "CreateTicket");
  const [showMessage, setShowMessage] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(t('requests.dialog.asking'));
  const [defaultTeam] = useState(currentState?.defaultTeam?.Id ?? null);
  const [defaultTemplateId, setDefaultTemplateId] = useState<number | null>(null);
  const [dataSourceList, setDataSourceList] = useState([]);
  const [customForms, setCustomForms] = useState<any[]>([]);
  const [activeForm, setActiveForm] = useState<any>(null);
  const [currentTemplate, setCurrentTemplate] = useState<any>({});
  const [isSummaryPage, setIsSummaryPage] = useState(false);
  const [isResetForm, setIsResetForm] = useState(false);
  const [goToNextForm, setGoToNextForm] = useState(false);
  const [ticketTitle, setTicketTitle] = useState("");
  const [continueButtonText, setContinueButtonText] = useState("");
  const [isDefaultTicketExperience, setIsDefaultTicketExperience] = useState(false);
  const [showSummaryCard, setShowSummaryCard] = useState(false);
  const [customFormListAttachmentsProps, setCustomFormListAttachmentsProps] = useState<CustomFormAttachmentsProps[]>([]); 
  const [isCustomFormLoading, setIsCustomFormLoading] = useState(false);

  const defaultTicketExpeienceCard = async () => {
    setLoadingMsg(t('common.loading'));
    setLoading(true);

    const results = await Promise.all([
      api.getTicketTemplates(`?$filter=IsDefault%20eq%20true&v=${new Date().getTime()}&$expand=TemplateForms($expand=Form)`),
      api.getDataSources(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}`)
    ]);

    setDataSourceList(results[1].data.value ?? []);
    const defaultTemplate = results[0].data.value[0];

    if (defaultTemplate?.CardResponseJson) {
      const cardPayload = JSON.parse(defaultTemplate.CardResponseJson);
      if (cardPayload)
        setAnswer(JSON.stringify(cardPayload));

      setDefaultTemplateId(defaultTemplate.Id);
    }

    if(defaultTemplate?.TemplateForms.length > 0){
      const templateForm = defaultTemplate.TemplateForms[0];
      const cardPayload = JSON.parse(templateForm.Form.Definition);
     
      if(cardPayload)
        setAnswer(JSON.stringify(cardPayload));

      setCustomFormTemplateData(defaultTemplate, setCurrentTemplate, setCustomForms, setActiveForm, currentState, dataSourceList);
      setContinueButtonText(templateForm.SubmitButtonText);
    }

    setShowSummaryCard(defaultTemplate.SummaryCardEnabled);
    setIsDefaultTicketExperience(true);
    setState(5);
    setLoading(false);
  }

  useEffect(() => {
    if (teamsConfig.RequestBehavior === "CreateTicket") {
      setRequest(" ");
      defaultTicketExpeienceCard();
    }
  }, [teamsConfig.RequestBehavior])

  const askQuestion = async () => {
    try {
      setLoading(true);
      setLoadingMsg(t('requests.dialog.asking'));
      const results = await Promise.all([
        api.getKnowledgeAnswer(request),
        api.getDataSources(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}`)
      ]);
      setDataSourceList(results[1].data.value ?? []);
      const answer = results[0].data;
      if (answer.AnswerType === "AddTicket") {
        await defaultTicketExpeienceCard();
        setShowMessage(true);
        setLoading(false);
        return;
      } else if (answer.AnswerType === "ApplyTemplate") {
        if (answer.Answer=="TemplateScoped"){
          setState(8);
          setLoading(false);
        }
        else
        {
          if ((answer?.Answer?.length ?? 0) > 0) {
            const cardPayload = JSON.parse(answer.Answer);
            setAnswer(JSON.stringify(cardPayload));
          }
          
          setState(2);
          setTemplate(answer.AnswerId);
          setShowMessage(false);
          setLoading(false);
          
          const {
            data: { value }
          } = await api.getTicketTemplates(`?$filter=Id eq ${answer.AnswerId}&v=${new Date().getTime()}&$expand=TemplateForms($expand=Form)`);
          setCustomFormTemplateData(value[0], setCurrentTemplate, setCustomForms, setActiveForm, currentState, dataSourceList);
          setShowSummaryCard(value[0]?.SummaryCardEnabled);
        }
        
        return;
      } else if (answer.AnswerType === "Text") {
        setState(1);
      }

      setAnswer(answer.Answer);
      setTemplate(answer.AnswerId);
    } catch {
      setState(3);
    }
    setShowMessage(false);
    setLoading(false);
  };

  const deflectTicket = async () => {
    setState(4);
    try {
      const result = await submitTicket(true, false);
      await api.addCommentToTicket(result.data.Id, { Body: `Deflected answer: ${answer}`, IsPublic: true, ModifiedById: 0 });
    } catch {
      // Ticket was deflected, we can ignore the error.
    }
  };

  const createTicket = async () => {
    let preventSubmit = false;
    if (state == 5 || state == 2)
      preventSubmit = CreateRequest_TriggerExecuteButton();

    if (preventSubmit) return;

    if (onCreateTicket) onCreateTicket();

    setLoading(true);
    setLoadingMsg(t('requests.dialog.creating'));

    try {

      const hasAttachments = customForms?.filter(x=>x.IsAnswered)?.some(f => f.CardAnswerJson?.[UploadFile.ElementId]?.length > 0) ?? false;      
      const ticket = await submitTicket(false, hasAttachments);

      if(hasAttachments && ticket.data){
        let customFormList = [];
        customForms.filter(x=>x.IsAnswered).forEach(form =>{
          customFormList.push({ TemplateFormId: form.TemplateFormId, FormName: form.Name, Order: form.Order, CardTemplateJson: JSON.stringify(form.CardTemplateJson), CardAnswerJson: JSON.stringify(form.CardAnswerJson) });
        });

        let updatedFormFiles: any[] = [];
        for(const formFiles of customFormListAttachmentsProps){
          const filesToAttach = await startFileUploadSession(api, uploadHelper, formFiles.Files, ticket.data.Id, false, "");
          updatedFormFiles.push({
            TemplateFormId: formFiles.TemplateFormId,
            Files: filesToAttach,
            Message: formFiles.Message,
            IsPrivate: formFiles.IsPrivate
          });
        }
        await api.attachWebCustomFormFiles(ticket.data.Id, customFormList, updatedFormFiles, true, false);
      }

      const requester = await getCachedUser(ticket.data.RequesterId);
      const result = await api.GetRequestsAndAffectedTickets(Number(requester?.Id));

      const apiData = {
        allTickets: JSON.parse(result?.data?.value),
        allStatus: currentState.ticketStatus
      };

      const _tickets = apiData.allTickets.map((ticket) => ({ ...ticket, Status: apiData.allStatus.find((status) => status.Id === ticket.StatusId) }));
      const [closed, active] = _.partition(_tickets, ({ Status }) => Status && (Status.Guid === STATUS_GUID.CLOSED || Status.Guid === STATUS_GUID.DEFLECTED || Status.Guid === STATUS_GUID.RESOLVED));

      setAppState(prevState => {
        prevState.tickets = active;
        prevState.closedTickets = closed;
        return prevState;
      });

      history.push(`/requests/${ticket.data.Id}`);
    } catch {
      setState(3);
    }

    if (onCreateComplete) onCreateComplete();
    setLoading(false);
  };

  const submitTicket = async (isDeflected: boolean, hasAttachments: boolean) => {
    const requester = await getUserFromToken();
    let source = await getPortalSource();

    const ticket: any = {
      Title: request,
      RequesterId: requester.Id,
      Requester: {
        EmailAddress: (requester.Email ?? requester.UserName),
        AadObjectId: requester.AadObjectId,
        UserPrincipalName: requester.UserName
      },
      TemplateId: templateId,
      SourceId: source.Id,
      TeamId: templateId ? null : defaultTeam
    };
    if ((templateId || state == 5) && !templateResponse[UploadFile.ElementId]) {
      ticket.CardAnswerJson = JSON.stringify(templateResponse);
      ticket.CardTemplateJson = RemoveRequest_ActionSet(answer, t);
      if (defaultTemplateId)
        ticket.TemplateId = defaultTemplateId;
    }
    ticket.CardAnswerJson = FormatCardAnswerJson(ticket.CardAnswerJson, ticket.CardTemplateJson);
    if (ticket.Title.trim() === "")  
        ticket.Title = ticketTitle;

    if (isDeflected) {
      const deflectedStatus = currentState.ticketStatus.find((status) => status.Guid === STATUS_GUID.DEFLECTED);

      if (deflectedStatus != null) {
        ticket.StatusId = deflectedStatus.Id;
        ticket.ResolutionDate = new Date();
      } else {
        throw new Error('Unable to get deflected status');
      }
    }

    if(customForms.length>0){
      let customFormList = [];

      customForms.filter(x=>x.IsAnswered).forEach(form =>{
        const cardAnswer = JSON.stringify(form.CardAnswerJson);
        const cardAnswerObj = FormatCardAnswerJson(cardAnswer, JSON.stringify(form.CardTemplateJson));
        customFormList.push({ TemplateFormId: form.TemplateFormId, FormName: form.Name, Order: form.Order, CardTemplateJson: JSON.stringify(form.CardTemplateJson), CardAnswerJson: cardAnswerObj });
      });

      if(!hasAttachments && customFormList.length>0)
        ticket.CustomForms = customFormList;
    }

    return await api.addRequest(ticket);
  };

  const onCancelClick = async() =>{
    if (onCancel) onCancel();
  }

  const updateActiveFormAnswer = () => {
    if (templateResponse["Ticket.Request"] != 'undefined' && templateResponse["Ticket.Request"]?.trim() !== "")  {
      setTicketTitle(templateResponse["Ticket.Request"])
    }
    const mergedAnswers = Object.assign(activeForm.CardAnswerJson, templateResponse);
    setActiveForm(preState => ({
        ...preState,
        CardAnswerJson: mergedAnswers
      }));
  }

  const resetForm = () => {
    const nextForm = customForms[0];
    const cardPayload = JSON.parse(parseCardTemplate(nextForm.CardTemplateJson, nextForm.CardAnswerJson, currentState, dataSourceList, null, request));
    
    setIsSummaryPage(false);
    setActiveForm(nextForm);
    setContinueButtonText(nextForm.SubmitButtonText);

    if(cardPayload)
      setAnswer(JSON.stringify(cardPayload));

    setIsResetForm(false);
  };

  const onContinueClick = async () =>{
    const formTemplate = parseIfJSON(activeForm.CardTemplateJson);
    const formAnswers = parseIfJSON(templateResponse);
       
    const validRequiredInput = validateRequiredInputs(formTemplate, formAnswers, currentState?.customProperties ?? []);
        
    if(validRequiredInput)
    {
      updateActiveFormAnswer();
      setGoToNextForm(true);
      setTemplateResponse({});
    }
  }
  
  const onStartOverClick = async()=>{
    setIsResetForm(true);
    setCustomForms(preState => ([]));
    setCustomFormTemplateData(currentTemplate, setCurrentTemplate, setCustomForms, setActiveForm, currentState, dataSourceList);
  }

  const onBackClick = async()=>{
    const allAnsweredForms = customForms.filter(x=>x.IsAnswered);
    const lastAnsweredIndex = _.findLastIndex(allAnsweredForms, x=>x.TemplateFormId==activeForm.TemplateFormId);
    const prevFormIndex = (!isSummaryPage) ? lastAnsweredIndex-1: (allAnsweredForms.length-1);
    const nextForm = allAnsweredForms[prevFormIndex];

    const cardPayload = JSON.parse(parseCardTemplate(nextForm.CardTemplateJson, nextForm.CardAnswerJson, currentState, dataSourceList, null, request));
    
    setActiveForm(nextForm);
    setIsSummaryPage(false)
    setContinueButtonText(nextForm.SubmitButtonText);
    setIsCustomFormLoading(true);
    setTemplateResponse(nextForm.CardAnswerJson);

    if(cardPayload)
      setAnswer(JSON.stringify(cardPayload));

    markFormsAsUnanswerd(allAnsweredForms, customForms, lastAnsweredIndex, setCustomForms);
  }

  const redirectToNextForm = async() =>{
    const selectedTemplateId = isDefaultTicketExperience ? defaultTemplateId : templateId;
    const result = await api.executeFormTransition(selectedTemplateId, activeForm.TemplateFormId, activeForm,0);
    const nextTemplateForm = result.data ?? null;

    if(nextTemplateForm){
      const nextForm = customForms[nextTemplateForm.Order] ?? null;
      const cardTemplate = JSON.stringify(nextForm.CardTemplateJson);
      const cardAnswers = nextForm.CardAnswerJson;
      const cardPayload = parseCardTemplate(cardTemplate, cardAnswers, currentState, dataSourceList);
     
      setActiveForm(nextForm);
      setContinueButtonText(nextForm.SubmitButtonText);
      
      if(cardPayload)
        setAnswer(cardPayload);
    }else{
      setIsSummaryPage(true);
      if(!showSummaryCard)
        createTicket();
    }
  }

  useEffect(()=>{
    if(!isResetForm)
      updateCustomFormListWithAnswers(customForms, activeForm, setCustomForms);
  
    if(goToNextForm){
      const moveToNextForm = async() => { await redirectToNextForm(); };
      moveToNextForm();
      setGoToNextForm(false);
    }
  },[activeForm]);
  
  useEffect(()=>{
    if(isResetForm){
      resetForm();
    }
  },[customForms]);

  useEffect(()=>{
    setContinueButtonText(activeForm?.SubmitButtonText);
  },[activeForm])

  return (
    <>
      {isLoading && (<Loader className='m-4' label={loadingMsg} />)}
      {!isLoading && (<>
        {(state === 2 || state === 5)
          ? (<>
           <div style={{ overflow: "visible !important" }}>
              {showMessage && <div style={{ marginBottom: '10px' }}>{t('requests.dialog.no-info')}</div>}
              {!isSummaryPage && <CreateRequestAdaptiveCard isEndUserPage={true} request={request} cardTemplate={answer} setCardTemplate={setAnswer} templateResponse={templateResponse} setTemplateResponse={setTemplateResponse} dataSourceList={dataSourceList} setCustomFormListAttachmentsProps={setCustomFormListAttachmentsProps} activeFormTemplateId={activeForm?.TemplateFormId} isCustomFormLoading={isCustomFormLoading} />}
              {isSummaryPage && <TicketCustomFormSection isEndUserPage={true} customForms={customForms.filter(x=>x.IsAnswered)} disabledFormInputs={true} template={currentTemplate} showSummaryInfo={true}/>}
            </div>
            <Flex gap='gap.small' className={'mt-4'} {...actionsFlexProps}>
              <Button content={t('common.buttons.cancel')} onClick={onCancelClick} style={{ ...(state === 5 ? { marginRight: 2 } : {}) }} />
              {(isSummaryPage || (activeForm?.Order > 0)) && <Button content={t('common.buttons.back')} onClick={onBackClick} style={{ ...(state === 5 ? { marginRight: 2 } : {}) }} />}
              {isSummaryPage && <Button  content={t('common.buttons.start-over')} onClick={onStartOverClick} style={{ ...(state === 5 ? { marginRight: 2 } : {}) }} />}
              {!isSummaryPage && <Button primary content={continueButtonText} onClick={onContinueClick} style={{ ...(state === 5 ? { marginRight: 2 } : {}) }} />}
              {isSummaryPage && <Button primary content={t('common.buttons.submit')} onClick={createTicket} style={{ ...(state === 5 ? { marginRight: 0 } : {}) }} /> }
              {!isSummaryPage && (activeForm?.Order == 0) && <Button primary content={t('common.buttons.something-else')} onClick={() => { setState(0); }} style={{ marginRight: 0 }} hidden={state !== 2} />}
            </Flex>
          </>)
          : (
            <>
              {state === 0 && (
                <LabelElement label={t('requests.dialog.title')} required={true}>
                  <TextArea autoFocus maxLength={1000} required style={{ height: 60 }} fluid onChange={(e, p) => { setRequest(p.value); }} />
                </LabelElement>
              )}
              {state === 1 && (
                <>
                  <Card elevated className='w-full-important card-border-accent h-unset-important'>
                    <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>{DOMPurify.sanitize(answer)}</Markdown>
                  </Card>
                  <div className='mt-4'>{t('requests.dialog.was-helpful')}</div>
                </>)}
              <div hidden={state !== 3}>{t('requests.dialog.oops')}</div>
              <div hidden={state !== 4}>{t('requests.dialog.glad-we-helped')}</div>

              <Flex gap='gap.small' className={'mt-4'} {...actionsFlexProps}>
                <Button primary content={t('requests.dialog.ask')} onClick={askQuestion} style={{ marginRight: 0 }} hidden={state !== 0} disabled={request.trim() === "" || (teamsConfig?.EnableMultiDepartmentPicker && !defaultTeam)} />
                <Button primary content={t('common.buttons.yes')} onClick={deflectTicket} hidden={state !== 1} />
                <Button primary content={t('requests.dialog.no-create')} onClick={defaultTicketExpeienceCard} hidden={state !== 1} />
                <Button primary content={t('common.buttons.something-else')} onClick={() => { setState(teamsConfig.RequestBehavior === "CreateTicket" ? 5 : 0); setTemplate(0); }} style={{ marginRight: 0 }} hidden={(state !== 1 && state !== 2) || teamsConfig.RequestBehavior === "CreateTicket"} />
              </Flex>
            </>)
        }
        {state === 8 && (<ScopeErrorMessage />)}
      </>)
      }
    </>);
}